import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { 
  BarChart3, Users, Building2, CheckCircle, XCircle, Clock,
  GraduationCap, Award, TrendingUp, BookOpen, FileText, Calendar
} from 'lucide-react';
import { toast } from 'sonner';
import { EstadisticasLB } from './EstadisticasLB';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Reportes = () => {
  const [activeTab, setActiveTab] = useState('estadisticas');
  const [resumen, setResumen] = useState(null);
  const [cumplimientoAreas, setCumplimientoAreas] = useState([]);
  const [lmsResumen, setLmsResumen] = useState(null);
  const [lmsPorPersona, setLmsPorPersona] = useState([]);
  const [lmsPorArea, setLmsPorArea] = useState([]);
  const [lmsPorCapacitacion, setLmsPorCapacitacion] = useState([]);
  const [evaluacionesRecientes, setEvaluacionesRecientes] = useState([]);
  const [estadisticas, setEstadisticas] = useState(null);
  const [estSubTab, setEstSubTab] = useState('resumen');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [resumenRes, areasRes, lmsResumenRes, lmsPersonaRes, lmsAreaRes, lmsCapRes, evalRecientesRes, estRes] = await Promise.all([
        axios.get(`${API}/cumplimiento/resumen`),
        axios.get(`${API}/cumplimiento/por-area`),
        axios.get(`${API}/reportes/lms/resumen`),
        axios.get(`${API}/reportes/lms/por-persona`),
        axios.get(`${API}/reportes/lms/por-area`),
        axios.get(`${API}/reportes/lms/por-capacitacion`),
        axios.get(`${API}/reportes/lms/evaluaciones-recientes`),
        axios.get(`${API}/reportes/estadisticas-lb`)
      ]);
      
      setResumen(resumenRes.data);
      setCumplimientoAreas(areasRes.data);
      setLmsResumen(lmsResumenRes.data);
      setLmsPorPersona(lmsPersonaRes.data);
      setLmsPorArea(lmsAreaRes.data);
      setLmsPorCapacitacion(lmsCapRes.data);
      setEvaluacionesRecientes(evalRecientesRes.data);
      setEstadisticas(estRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Reportes</h1>
        <p className="text-slate-500">Monitoreo de capacitaciones y cumplimiento</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 pb-2 overflow-x-auto">
        {[
          { id: 'estadisticas', label: 'Estadísticas LB' },
          { id: 'general', label: 'Cumplimiento General' },
          { id: 'lms', label: 'Resultados LMS' },
          { id: 'personas', label: 'Por Persona' },
          { id: 'capacitaciones', label: 'Por Capacitación' }
        ].map(tab => (
          <button key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors whitespace-nowrap ${
              activeTab === tab.id 
                ? 'bg-teal-100 text-teal-700 border-b-2 border-teal-500' 
                : 'text-slate-600 hover:bg-slate-100'
            }`}
          >{tab.label}</button>
        ))}
      </div>

      {/* Tab: Estadísticas LB */}
      {activeTab === 'estadisticas' && estadisticas && <EstadisticasLB data={estadisticas} subTab={estSubTab} setSubTab={setEstSubTab} />}

      {/* Tab: Cumplimiento General */}
      {activeTab === 'general' && (
        <div className="space-y-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{resumen?.total_personas || 0}</p>
                    <p className="text-xs text-slate-500">Total Personas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{resumen?.cumplidas || 0}</p>
                    <p className="text-xs text-slate-500">Cumplidas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                    <Clock className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{resumen?.por_vencer_30_dias || 0}</p>
                    <p className="text-xs text-slate-500">Por Vencer</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-500 rounded-lg flex items-center justify-center">
                    <XCircle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{resumen?.vencidas || 0}</p>
                    <p className="text-xs text-slate-500">Vencidas</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cumplimiento General */}
          <Card className="border border-slate-200 shadow-sm">
            <CardContent className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-slate-900">Cumplimiento General</h3>
                  <p className="text-sm text-slate-500">Porcentaje de capacitaciones completadas</p>
                </div>
                <span className="text-3xl font-bold text-teal-500">
                  {resumen?.porcentaje_cumplimiento || 0}%
                </span>
              </div>
              <Progress value={resumen?.porcentaje_cumplimiento || 0} className="h-3" />
            </CardContent>
          </Card>

          {/* Cumplimiento por Área */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-teal-500" />
                Cumplimiento por Área
              </CardTitle>
            </CardHeader>
            <CardContent>
              {cumplimientoAreas.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No hay datos disponibles</p>
              ) : (
                <div className="space-y-4">
                  {cumplimientoAreas.map((area) => (
                    <div key={area.area_id} className="space-y-2" data-testid={`area-cumplimiento-${area.area_id}`}>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-slate-400" />
                          <span className="font-medium text-slate-700">{area.area_nombre}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <span className="text-sm text-slate-500">
                            {area.cumplidas}/{area.total}
                          </span>
                          <span className="text-lg font-bold text-slate-900">
                            {area.porcentaje}%
                          </span>
                        </div>
                      </div>
                      <Progress value={area.porcentaje} className="h-2" />
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Resultados LMS */}
      {activeTab === 'lms' && (
        <div className="space-y-6">
          {/* LMS Stats */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{lmsResumen?.total_cursos || 0}</p>
                    <p className="text-xs text-slate-500">Cursos Activos</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{lmsResumen?.total_evaluaciones || 0}</p>
                    <p className="text-xs text-slate-500">Evaluaciones</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-emerald-500 rounded-lg flex items-center justify-center">
                    <Award className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{lmsResumen?.tasa_aprobacion || 0}%</p>
                    <p className="text-xs text-slate-500">Tasa Aprobación</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border border-slate-200 shadow-sm">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-slate-900">{lmsResumen?.promedio_nota || 0}%</p>
                    <p className="text-xs text-slate-500">Nota Promedio</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* LMS por Área */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Building2 className="w-5 h-5 text-teal-500" />
                Rendimiento por Área
              </CardTitle>
            </CardHeader>
            <CardContent>
              {lmsPorArea.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No hay datos disponibles</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 font-medium text-slate-600">Área</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Personas</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Evaluaciones</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Aprobados</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Tasa</th>
                        <th className="text-center py-3 px-2 font-medium text-slate-600">Promedio</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lmsPorArea.map((area) => (
                        <tr key={area.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-2 font-medium">{area.nombre}</td>
                          <td className="py-3 px-2 text-center">{area.total_personas}</td>
                          <td className="py-3 px-2 text-center">{area.total_evaluaciones || 0}</td>
                          <td className="py-3 px-2 text-center text-emerald-600">{area.cursos_aprobados || 0}</td>
                          <td className="py-3 px-2 text-center">
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              area.tasa_aprobacion >= 80 ? 'bg-emerald-100 text-emerald-700' :
                              area.tasa_aprobacion >= 60 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {area.tasa_aprobacion}%
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">{area.promedio_nota || '-'}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Evaluaciones Recientes */}
          <Card className="border border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="w-5 h-5 text-teal-500" />
                Evaluaciones Recientes
              </CardTitle>
            </CardHeader>
            <CardContent>
              {evaluacionesRecientes.length === 0 ? (
                <p className="text-slate-500 text-center py-8">No hay evaluaciones registradas</p>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {evaluacionesRecientes.slice(0, 15).map((eval_item) => (
                    <div 
                      key={eval_item.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        eval_item.aprobado ? 'bg-emerald-50' : 'bg-red-50'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                          eval_item.aprobado ? 'bg-emerald-100' : 'bg-red-100'
                        }`}>
                          {eval_item.aprobado ? (
                            <CheckCircle className="w-4 h-4 text-emerald-600" />
                          ) : (
                            <XCircle className="w-4 h-4 text-red-600" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium text-slate-900 text-sm">
                            {eval_item.persona_nombre} {eval_item.persona_apellido}
                          </p>
                          <p className="text-xs text-slate-500">{eval_item.capacitacion_nombre}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${eval_item.aprobado ? 'text-emerald-600' : 'text-red-600'}`}>
                          {eval_item.nota}%
                        </p>
                        <p className="text-xs text-slate-500">
                          {eval_item.fecha_completado ? new Date(eval_item.fecha_completado).toLocaleDateString('es-CL') : '-'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tab: Por Persona */}
      {activeTab === 'personas' && (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Users className="w-5 h-5 text-teal-500" />
              Reporte por Persona
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lmsPorPersona.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No hay datos disponibles</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-2 font-medium text-slate-600">Persona</th>
                      <th className="text-left py-3 px-2 font-medium text-slate-600">Área</th>
                      <th className="text-left py-3 px-2 font-medium text-slate-600">Cargo</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Evaluaciones</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Aprobados</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Promedio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lmsPorPersona.map((persona) => (
                      <tr key={persona.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium">{persona.nombre} {persona.apellido}</p>
                            <p className="text-xs text-slate-500">{persona.rut}</p>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-slate-600">{persona.area_nombre || '-'}</td>
                        <td className="py-3 px-2 text-slate-600">{persona.cargo_nombre || '-'}</td>
                        <td className="py-3 px-2 text-center">{persona.total_evaluaciones || 0}</td>
                        <td className="py-3 px-2 text-center">
                          <span className="text-emerald-600 font-medium">{persona.cursos_aprobados || 0}</span>
                        </td>
                        <td className="py-3 px-2 text-center">
                          {persona.promedio_nota ? (
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              persona.promedio_nota >= 80 ? 'bg-emerald-100 text-emerald-700' :
                              persona.promedio_nota >= 60 ? 'bg-amber-100 text-amber-700' :
                              'bg-red-100 text-red-700'
                            }`}>
                              {persona.promedio_nota}%
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Tab: Por Capacitación */}
      {activeTab === 'capacitaciones' && (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <GraduationCap className="w-5 h-5 text-teal-500" />
              Reporte por Capacitación
            </CardTitle>
          </CardHeader>
          <CardContent>
            {lmsPorCapacitacion.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No hay datos disponibles</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-2 font-medium text-slate-600">Capacitación</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Modalidad</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Preguntas</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Evaluaciones</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Aprobados</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Tasa</th>
                      <th className="text-center py-3 px-2 font-medium text-slate-600">Promedio</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lmsPorCapacitacion.map((cap) => (
                      <tr key={cap.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-2">
                          <div>
                            <p className="font-medium">{cap.nombre}</p>
                            <p className="text-xs text-slate-500">
                              {cap.es_transversal ? 'Transversal' : cap.area_nombre || cap.actividad_nombre || '-'}
                            </p>
                          </div>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            cap.modalidad === 'e-learning' ? 'bg-blue-100 text-blue-700' :
                            cap.modalidad === 'presencial' ? 'bg-green-100 text-green-700' :
                            'bg-purple-100 text-purple-700'
                          }`}>
                            {cap.modalidad}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-center">{cap.num_preguntas || 0}</td>
                        <td className="py-3 px-2 text-center">{cap.total_evaluaciones || 0}</td>
                        <td className="py-3 px-2 text-center text-emerald-600">{cap.aprobados || 0}</td>
                        <td className="py-3 px-2 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            cap.tasa_aprobacion >= 80 ? 'bg-emerald-100 text-emerald-700' :
                            cap.tasa_aprobacion >= 60 ? 'bg-amber-100 text-amber-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {cap.tasa_aprobacion}%
                          </span>
                        </td>
                        <td className="py-3 px-2 text-center">{cap.promedio_nota || '-'}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
